import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/lib/auth.store';
import { api } from '@/lib/axios';
import {
  FeldStatus,
  mitStand,
  useFeld,
  useSpeicherstelle,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern';
import { InstallGuide } from '@symphenia/ui-components';
import { INSTALL_TEXTS } from '@/components/install-texts';
import { usersApi } from '@/lib/users/users.api';
import type { ChangePasswordPayload } from '@/lib/users/user.types';
import { MailConnectionCard } from '../components/MailConnectionCard';
import { MailSignatureCard } from '../components/MailSignatureCard';
import { ZweiFaktorKarte } from '../components/ZweiFaktorKarte';
import { authApi } from '@/lib/auth.api';
import { merkeFirma } from '@/lib/firma';
import { bestaetige } from '@/lib/dialoge';

const LOCALES = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring';

const labelClass = 'mb-1 block text-sm font-medium text-foreground';

/**
 * AP-36 Phase E: Das Profil speichert **ohne Knopf** — jedes Feld beim
 * Verlassen, die Sprache sofort. Passwort und PIN bleiben Handlungen mit
 * eigenem Knopf (Zugangsdaten ändern).
 */
export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user, loadUser } = useAuthStore();

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: 'meinProfil',
    bezeichnung: t('users.profile'),
    daten: user as unknown as Datensatz | null,
    // Das Profil steht verschachtelt, geschrieben wird flach — wie das PATCH es nimmt.
    werte: (antwort) => {
      const u = (antwort ?? {}) as Datensatz;
      const profil = (u.profile ?? {}) as Datensatz;
      return {
        firstName: profil.firstName ?? null,
        lastName: profil.lastName ?? null,
        phone: profil.phone ?? null,
        jobTitle: profil.jobTitle ?? null,
        avatar: profil.avatar ?? null,
        preferredLocale: u.preferredLocale ?? u.locale ?? 'de',
        updatedAt: u.updatedAt,
      };
    },
    schreibe: async (patch, stand) => {
      const { data } = await api.patch<Datensatz>('/users/me/profile', patch, mitStand(stand));
      // Die Sprache wirkt sofort, und der Kopf der App liest den Benutzer neu.
      if (typeof patch.preferredLocale === 'string' && patch.preferredLocale !== i18n.language) {
        await i18n.changeLanguage(patch.preferredLocale);
      }
      await loadUser();
      return data;
    },
    keepalive: { url: '/users/me/profile', method: 'PATCH' },
  }) as Speicherstelle<unknown>;

  const [pwForm, setPwForm] = useState<ChangePasswordPayload & { confirm: string }>({
    currentPassword: '',
    newPassword: '',
    confirm: '',
  });

  const [pwSaving, setPwSaving] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  const handlePasswordSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirm) {
      setPwError(t('auth.passwordMismatch'));
      return;
    }
    setPwSaving(true);
    setPwError(null);
    try {
      await usersApi.changePassword({
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });
      setPwSuccess(true);
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
      setTimeout(() => setPwSuccess(false), 3000);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('common.error');
      setPwError(message);
    } finally {
      setPwSaving(false);
    }
  };

  const initials = (
    (user?.profile?.firstName?.[0] ?? '') + (user?.profile?.lastName?.[0] ?? '')
  ).toUpperCase() || user?.email[0].toUpperCase();

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">{t('nav.profile')}</h1>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Avatar + info */}
        <div className="lg:col-span-1">
          <div className="rounded-lg border bg-card p-6 shadow-sm text-center">
            {user?.profile?.avatar ? (
              <img
                src={user.profile.avatar}
                alt="Avatar"
                className="mx-auto mb-3 h-20 w-20 rounded-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div className="mx-auto mb-3 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-2xl font-bold text-primary">
                {initials}
              </div>
            )}
            <p className="font-semibold text-foreground">
              {user?.profile?.firstName} {user?.profile?.lastName}
            </p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <span className="mt-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              {user?.roles.map((r) => r.name).join(', ') || '—'}
            </span>
          </div>

          {/* AP-17 (Baustein D): App-Installations-Anleitung inkl. iOS-Fallback */}
          <InstallGuide texts={INSTALL_TEXTS} className="mt-6" />
        </div>

        {/* Profile form */}
        <div className="lg:col-span-2 space-y-6">
          <Profilfelder stelle={stelle} />

          {/* AP-10 Nachtrag 5: Signatur unter Mails aus dem Exportversand */}
          <MailSignatureCard />

          {/* AP-10 Nachtrag 1: eigenes Microsoft-365-Postfach */}
          <MailConnectionCard />

          {/* AP-38-E: die eigene Zwei-Faktor-Anmeldung */}
          <ZweiFaktorKarte />

          {/* AP-38-C: Eine externe Person gibt ihren Zugang selbst zurück */}
          <ZugangZurueckgeben />

          {/* Change password */}
          <form
            onSubmit={handlePasswordSave}
            className="rounded-lg border bg-card p-6 shadow-sm"
          >
            <h2 className="mb-4 text-base font-semibold text-foreground">
              {t('auth.changePassword')}
            </h2>

            <div className="space-y-4">
              <div>
                <label className={labelClass}>{t('auth.currentPassword')}</label>
                <input
                  type="password"
                  required
                  value={pwForm.currentPassword}
                  onChange={(e) => setPwForm((f) => ({ ...f, currentPassword: e.target.value }))}
                  className={inputClass}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>{t('auth.newPassword')}</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={pwForm.newPassword}
                    onChange={(e) => setPwForm((f) => ({ ...f, newPassword: e.target.value }))}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('auth.passwordConfirm')}</label>
                  <input
                    type="password"
                    required
                    value={pwForm.confirm}
                    onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            {pwError && (
              <p className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {pwError}
              </p>
            )}
            {pwSuccess && (
              <p className="mt-4 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
                {t('users.passwordChanged')}
              </p>
            )}

            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                disabled={pwSaving}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {pwSaving ? t('common.loading') : t('auth.changePassword')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/**
 * AP-38-C — Nur für eine **externe** Person (etwa die Lohnverrechnung einer
 * Steuerberatung): Sie beendet ihre Arbeit für diese Firma selbst. Danach wechselt
 * die Oberfläche in eine andere Firma der Person, sonst meldet sie ab.
 */
function ZugangZurueckgeben() {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  if (user?.kind !== 'EXTERNAL') return null;

  async function zurueckgeben() {
    if (!(await bestaetige(t('users.leaveConfirm'), { ok: t('users.leaveButton'), gefaehrlich: true }))) return;
    setLaeuft(true);
    setFehler(null);
    try {
      const res = await usersApi.leave();
      const naechste = res.data.data.naechsteFirma;
      if (naechste) {
        await authApi.switchFirma(naechste);
        merkeFirma(naechste);
        window.location.assign('/');
      } else {
        await logout();
      }
    } catch (err) {
      setLaeuft(false);
      const m = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
      setFehler(typeof m === 'string' ? m : t('users.leaveFailed'));
    }
  }

  return (
    <div className="rounded-lg border bg-card p-6 shadow-sm">
      <h2 className="mb-2 text-base font-semibold text-foreground">{t('users.leaveTitle')}</h2>
      <p className="mb-4 text-sm text-muted-foreground">{t('users.leaveText')}</p>
      {fehler && <p className="mb-3 text-sm text-destructive">{fehler}</p>}
      <button
        type="button"
        onClick={zurueckgeben}
        disabled={laeuft}
        className="rounded-md border border-destructive/40 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
      >
        {laeuft ? t('common.loading') : t('users.leaveButton')}
      </button>
    </div>
  );
}

/** Die Felder des Profils — jedes speichert beim Verlassen, die Sprache sofort. */
function Profilfelder({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const { t } = useTranslation();
  const firstName = useFeld(stelle, 'firstName', { label: t('users.firstName') });
  const lastName = useFeld(stelle, 'lastName', { label: t('users.lastName') });
  const phone = useFeld(stelle, 'phone', { label: t('users.phone') });
  const jobTitle = useFeld(stelle, 'jobTitle', { label: t('users.jobTitle') });
  const avatar = useFeld(stelle, 'avatar', {
    label: t('users.avatar'),
    pruefe: (w) => (w && !/^https?:\/\/\S+$/i.test(w) ? 'Keine gültige Adresse (https://…)' : null),
  });
  const sprache = useFeld(stelle, 'preferredLocale', { art: 'auswahl', label: t('users.preferredLocale'), pflicht: true });

  const zeile = (f: ReturnType<typeof useFeld<string | null>>, label: string, art = 'text', platzhalter?: string) => (
    <div>
      <label className={labelClass}>{label}</label>
      <input type={art} placeholder={platzhalter} className={inputClass} {...f.props} />
      <FeldStatus status={f} />
    </div>
  );

  return (
    <div className="rounded-lg border bg-card p-6 shadow-sm">
      <h2 className="mb-4 text-base font-semibold text-foreground">{t('users.profile')}</h2>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {zeile(firstName, t('users.firstName'))}
          {zeile(lastName, t('users.lastName'))}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {zeile(phone, t('users.phone'), 'tel')}
          {zeile(jobTitle, t('users.jobTitle'))}
        </div>

        {zeile(avatar, `${t('users.avatar')} (URL)`, 'url', 'https://example.com/avatar.jpg')}

        <div>
          <label className={labelClass}>{t('users.preferredLocale')}</label>
          <select className={inputClass} {...sprache.props}>
            {LOCALES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
          <FeldStatus status={sprache} />
        </div>
      </div>
    </div>
  );
}
